import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Form, Input, Button, Alert, Spin, Typography } from 'antd';
import { STATUS } from '../../../../redux/constants';
import {
  showNotification,
  simpleErrorNotification,
  successNotification,
} from '../../../../shared-components/Notifications';
import AuthLayout from '../../../../shared-components/AuthLayout';
import styles from './confirm-reset-password.module.sass';
import { passwordValidator } from './validator';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkToken,
  confirmPasswordReset,
} from '../../../../redux/actions/auth';
import Auth from '../../../../redux/reducers/auth';
import { changeTitle } from '../../../../redux/actions/app';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';

function PasswordRequirements({ requirements }) {
  const { Text } = Typography;
  let sortedReq = Object.keys(requirements).map((k) => {
    return {
      key: k,
      ...requirements[k],
    };
  });
  sortedReq.sort((a, b) => a.order - b.order);

  const colors = {
    [STATUS.ERROR]: '#ff4d4f',
    [STATUS.SUCCESS]: '#6abe39',
    [STATUS.IDLE]: '',
  };
  return (
    <>
      {sortedReq.map((req) => (
        <Text
          style={{
            color: colors[req.status],
          }}
          key={req.key}
        >
          {req.status !== 'idle' && (
            <>
              {req.status === 'success' && (
                <CheckCircleFilled style={{ marginRight: '.3rem' }} />
              )}
              {req.status === 'error' && (
                <CloseCircleFilled style={{ marginRight: '.3rem' }} />
              )}
            </>
          )}
          {req.description}
        </Text>
      ))}
    </>
  );
}

function ConfirmResetPasswordForm({
  passwordConfirmationStatus,
  setRequirements,
  uid,
  token,
  setError,
  errorMessages,
}) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (passwordConfirmationStatus == 'error') {
      setError(errorMessages);
    }
  }, [passwordConfirmationStatus]);

  const handleFormSubmit = (values) => {
    const data = {
      uid,
      token,
      new_password: values.password,
      re_new_password: values.confirm,
    };
    dispatch(confirmPasswordReset(data));
  };
  return (
    <Form
      initialValues={{
        remember: true,
      }}
      layout={'vertical'}
      onFinish={handleFormSubmit}
    >
      <Form.Item
        name="password"
        label="Password (required)"
        rules={[
          {
            required: true,
            message: 'Please enter your password',
          },
          ({ getFieldValue }) => passwordValidator(setRequirements),
        ]}
        hasFeedback
      >
        <Input.Password
          placeholder={'Enter password'}
          className={styles['password-input']}
          onChange={(e) => setError('')}
        />
      </Form.Item>
      <Form.Item
        name="confirm"
        label="Confirm Password (required)"
        dependencies={['password']}
        hasFeedback
        rules={[
          ({ getFieldValue }) => ({
            validator(rule, value) {
              // if (!value || getFieldValue('password') === value) {
              if (
                getFieldValue('password') === '' ||
                getFieldValue('password') === value
              ) {
                return Promise.resolve();
              }

              return Promise.reject('Passwords are not matching!');
            },
          }),
        ]}
      >
        <Input.Password
          placeholder={'Confirm password'}
          className={styles['password-input']}
          onChange={(e) => setError('')}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={passwordConfirmationStatus === STATUS.RUNNING}
          className="login-form-button"
        >
          Set Password
        </Button>
      </Form.Item>
    </Form>
  );
}

function NewConfirmResetPassword() {
  let { uid, token } = useParams();
  const { Title } = Typography;
  const history = useHistory();
  const dispatch = useDispatch();
  const tokenStatus = useSelector((state) => state.auth.checkTokenStatus);
  const passwordConfirmationStatus = useSelector(
    (state) => state.auth.passwordConfirmationStatus,
  );
  const errorMessages = useSelector((state) => state.auth.errorMessages);
  const [error, setError] = useState('');

  const [requirements, setRequirements] = useState({
    length: {
      description: '8 characters',
      status: STATUS.IDLE,
      order: 0,
    },
    '1_lower_char': {
      description: '1 lower case',
      status: STATUS.IDLE,
      order: 1,
    },
    '1_upper_char': {
      description: '1 upper case',
      status: STATUS.IDLE,
      order: 2,
    },
    '1_special_char': {
      description: '1 special character(#?!_@$%^&*-)',
      status: STATUS.IDLE,
      order: 3,
    },
    '1_digit_char': {
      description: '1 digit required',
      status: STATUS.IDLE,
      order: 4,
    },
  });

  useEffect(() => {
    document.title = 'Set Password - Silent Push';
    dispatch(changeTitle('Set Password'));
  }, []);

  useEffect(() => {
    if (uid && token) {
      dispatch(checkToken({ uid, token }));
    }
  }, [uid, token]);

  const renderDescription = () => {
    return (
      <div className={styles.passwordRequirements}>
        <Title level={5}>
          Create your password. Password must have minimum:
        </Title>
        <PasswordRequirements requirements={requirements} />
      </div>
    );
  };

  if (passwordConfirmationStatus === STATUS.SUCCESS) {
    return (
      <AuthLayout
        style={{ width: 491 }}
        title="Password has been set up successfully!"
        description={
          'You can now login to the Silent Push app with your credentials.'
        }
      >
        <Button
          onClick={() => {
            history.push('/login');
          }}
          style={{ marginTop: 8 }}
          type={'primary'}
        >
          Login
        </Button>
      </AuthLayout>
    );
  }
  if (tokenStatus === STATUS.LOADED) {
    return (
      <AuthLayout
        title={'Set Up a Password'}
        description={renderDescription()}
        error={error}
      >
        <ConfirmResetPasswordForm
          passwordConfirmationStatus={passwordConfirmationStatus}
          setRequirements={setRequirements}
          uid={uid}
          token={token}
          setError={setError}
          errorMessages={errorMessages}
        />
      </AuthLayout>
    );
  }
  if (tokenStatus === STATUS.ERROR) {
    return (
      <AuthLayout
        title="Your invitation link has expired"
        description={
          'Please contact your organisation admin in the Silent Push app to reset your account.'
        }
      />
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        height: '95vh',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Spin />{' '}
    </div>
  );
}

export default NewConfirmResetPassword;
